import { useEffect, useRef, useState } from "react";

export default function useFormulaDraft({ userId, recipeId, ready, values, stamp, restore }) {
  const key = userId ? `bench-formula-draft:${userId}:${recipeId || "new"}` : null;
  const serialized = JSON.stringify(values);
  const [baseline, setBaseline] = useState(null);
  const [pending, setPending] = useState(null);
  const [storageError, setStorageError] = useState("");
  const initialized = useRef(null);
  const latest = useRef(serialized);
  latest.current = serialized;
  useEffect(() => {
    if (!ready || !key || initialized.current === key) return;
    initialized.current = key;
    setBaseline(serialized);
    try {
      const raw = localStorage.getItem(key);
      if (raw) setPending(JSON.parse(raw));
    } catch { setStorageError("Browser draft storage is unavailable. Save to the server before leaving."); }
  }, [key, ready, serialized]);
  const dirty = baseline !== null && serialized !== baseline;
  useEffect(() => {
    if (!ready || !key || initialized.current !== key || baseline === null || pending) return;
    try {
      if (dirty) localStorage.setItem(key, JSON.stringify({ values, stamp, savedAt: new Date().toISOString() }));
      else localStorage.removeItem(key);
      setStorageError("");
    } catch { setStorageError("Could not store a recovery draft on this browser. Keep this tab open until Save succeeds."); }
  }, [serialized, stamp, ready, key, baseline, pending, dirty]); // synchronous draft write on each committed edit
  useEffect(() => {
    if (!dirty) return;
    const before = e => { e.preventDefault(); e.returnValue = ""; };
    const click = e => {
      const a = e.target.closest?.("a[href]");
      if (a && !a.hash && !a.hasAttribute("download") && a.target !== "_blank" &&
          !window.confirm("You have unsaved formula edits. Leave this page? Your browser recovery draft will remain.")) {
        e.preventDefault(); e.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", before);
    document.addEventListener("click", click, true);
    return () => { window.removeEventListener("beforeunload", before); document.removeEventListener("click", click, true); };
  }, [dirty]);
  return {
    dirty, pending, storageError,
    recover() { if (pending) restore(pending.values, pending.stamp); setPending(null); },
    discard() { try { localStorage.removeItem(key); } catch {} setPending(null); },
    saved(captured) {
      setBaseline(JSON.stringify(captured));
      if (latest.current === JSON.stringify(captured)) {
        try { localStorage.removeItem(key); } catch {}
      }
    },
  };
}
